<template>
  <v-container fluid grid-list-xl py-0 px-0 ma-0>
    <v-layout row wrap>
      <!-- Technique -->
      <v-flex sm4 py-1>
        <techniques
          :filters="filters"
          :techniques="techniques"
          :loading="loading"
          @change="handleFiltersChanged"
        />
      </v-flex>

      <!-- Theme -->
      <v-flex sm4 py-1>
        <themes
          :filters="filters"
          :themes="themes"
          :loading="loading"
          @change="handleFiltersChanged"
        />
      </v-flex>

      <!-- Size -->
      <v-flex sm4 py-1>
        <sizes :filters="filters" :sizes="sizes" :loading="loading" @change="handleFiltersChanged" />
      </v-flex>

      <!-- Orientation -->
      <v-flex sm4 py-1>
        <orientations
          :filters="filters"
          :orientations="orientations"
          :loading="loading"
          @change="handleFiltersChanged"
        />
      </v-flex>

      <!-- Price -->
      <v-flex sm4 py-1>
        <price :filters="filters" :loading="loading" @change="handleFiltersChanged" />
      </v-flex>

      <!-- Color -->
      <v-flex sm4 py-1>
        <colors
          :filters="filters"
          :colors="colors"
          :loading="loading"
          @change="handleFiltersChanged"
        />
      </v-flex>
    </v-layout>

    <!-- Filter tags -->
    <filters-tags class="mt-4" v-if="!$vuetify.breakpoint.xsOnly" :filters="filters" />
  </v-container>
</template>

<script>
import "vue-slider-component/theme/default.css";
import { RepositoryFactory } from "@/services/RepositoryFactory";
import { ErrorHandler } from "@/utils/ErrorHandler";
import { SearchFiltersTranslations } from "@/services/search/SearchFiltersTranslations";
import { Color } from "@/services/color/Color";
import { Size } from "@/services/size/Size";
import { SearchFilters } from "@/services/search/SearchFilters";
import { ROUTE_QUERY, ROUTE_QUERY_EVENT } from "@/AppKeys";
import debounce from "@/utils/TinyDebounce";

import FiltersTags from "./FiltersTags";
import Techniques from "./Techniques";
import Themes from "./Themes";
import Sizes from "./Sizes";
import Orientations from "./Orientations";
import Colors from "./Colors";
import Price from "./Price";

const translationsRepo = RepositoryFactory.translations();
const handler = new ErrorHandler();

export default {
  components: {
    Techniques,
    Themes,
    Sizes,
    Orientations,
    Colors,
    Price,
    FiltersTags
  },

  inject: [ROUTE_QUERY],

  data() {
    return {
      loading: false,
      techniques: [], // list of translated techniques
      themes: [], // list of translated themes
      sizes: [], // list of translated sizes
      colors: [],
      filters: new SearchFilters(),
      filtersTranslations: null
    };
  },

  computed: {
    orientations() {
      return [
        { id: "portrait", name: this.$t("ARTWORK.PORTRAIT") },
        { id: "square", name: this.$t("ARTWORK.SQUARE") },
        { id: "landscape", name: this.$t("ARTWORK.LANDSCAPE") }
      ];
    }
  },

  created() {
    this.init();
    this.$root.$on(ROUTE_QUERY_EVENT, () => {
      console.log("ON Route query change");
      this.queryMapFilters();
    });
  },

  beforeDestroy() {
    this.$root.$off(ROUTE_QUERY_EVENT);
  },

  watch: {
    "filters.pricesSelected.price_min": function(val) {
      if (val == null) {
        this.customPrice = { min: 0, max: 7500 };
        this.priceRange = [0, 7500];
      }
    },
  },

  methods: {
    async init() {
      this.loading = true;

      await this.setupFilterTranslations();

      this.queryMapFilters();

      this.loading = false;
    },

    handleFiltersChanged() {
      const routeQuery = this[ROUTE_QUERY];
      // Update searchText
      this.filters.searchText = this.$route.query.keyword;
      const payload = this.filters.toQuery();

      delete payload.for_sale;
      delete payload.video;
      delete payload.ready_to_hang;
      delete payload.framed;
      delete payload.sort_by;

      routeQuery.push(payload);
    },

    setupTechniques(categories) {
      if (! this.techniques.length) {
        categories.forEach((cat, i) => {
          const children = cat.techniques.map((technique, j) => {
            return {
              key: `${technique.id}_${i}_${j}`,
              id: technique.id,
              name: technique.name,
              slug: technique.slug
            };
          });

          this.techniques.push({
            id: `${cat.id}_${i}`,
            name: cat.name,
            slug: cat.slug,
            children: children
          });
        });
      }
    },

    async setupFilterTranslations() {
      try {
        if (!this.filtersTranslations) {
          const res = await translationsRepo.getTranslations(
            "categories,themes,size-ranges,colors"
          );

          this.themes = res.themes;
          this.sizes = Size.sizesFromArray(res.size_ranges);
          this.colors = Color.colorsFromObject(res.colors);

          this.filtersTranslations = new SearchFiltersTranslations({
            sizes: this.sizes,
            colors: this.colors,
            orientations: this.orientations,
            themes: this.themes,
            techs: this.techniques
          });

          this.setupTechniques(res.categories);
        }
      } catch (error) {
        console.log("Error", error);
      }
    },

    async queryMapFilters() {
      const query = this.$route.query;
      if (!query) return;

      await this.setupFilterTranslations();

      const filtersFromQuery = new SearchFilters()
        .fromQuery(query)
        .translate(this.filtersTranslations);

      this.filters.techniquesSelected = filtersFromQuery.techniquesSelected;
      this.filters.orientationsSelected = filtersFromQuery.orientationsSelected;
      this.filters.themesSelected = filtersFromQuery.themesSelected;
      this.filters.pricesSelected = filtersFromQuery.pricesSelected;
      this.filters.sizesSelected = filtersFromQuery.sizesSelected;
      this.filters.colorSelected = filtersFromQuery.colorSelected;
      this.filters.sortType = filtersFromQuery.sortType || "artworks";
      this.filters.searchText = filtersFromQuery.searchText;
      this.filters.page = filtersFromQuery.page || 1;
      this.filters.framed = filtersFromQuery.framed || false;
      this.filters.readyToHang = filtersFromQuery.readyToHang || false;
      this.filters.forSale = filtersFromQuery.forSale || false;
      this.filters.video = filtersFromQuery.video || false;
    }
  }
};
</script>

<style lang="scss">
.search-select-item {
  .v-input__slot {
    // background-color: #fafafa !important;
    border-bottom: 1px solid;
    margin-bottom: 0px;
    padding-left: 0px !important;
    padding-right: 0px !important;
    cursor: pointer !important;

    .v-select__slot {
      margin-bottom: -10px;

      label.v-label {
        color: #000000;
      }

      .v-input__append-inner {
        // Clearlable icon size
        .v-icon.fa-times {
          font-size: 1.3rem;
        }
      }
    }
  }
}

.search-menu-content {
  .v-list__tile {
    pointer-events: none;
    height: 40px;

    .checkbox-item,
    .v-input__control,
    label {
      width: 100%;
    }

    .v-input__control {
      pointer-events: all;
    }
  }

  .v-divider {
    margin-top: 10px;
    margin-bottom: 5px;
  }
}

// TODO Small hack to display dropdown checkbox gap match to others
.multi-select-search-menu {
  .v-input--selection-controls:not(.v-input--hide-details) .v-input__slot {
    margin-bottom: 0px;
  }
  .v-list__tile__content {
    margin-left: -24px;
  }
}

.search-price {
  .v-input__control {
    width: 90px;
    border: 1px solid #979797;
    min-height: 36px !important;
    border-radius: 4px;
  }
}

.v-treeview-node {
  margin-bottom: 8px;

  .v-treeview-node__toggle {
    position: absolute;
    right: 10px;
  }

  .v-treeview-node--leaf {
    margin-left: 24px;
  }
}
</style>
